import React, {
	createContext,
	useCallback,
	useContext,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import {
	renderChildrenOrChildrenFunction,
	type IContextChildren,
	type IContextRef,
} from '@netx/core-next';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {
	ConfirmationDialog,
	type ConfirmationOptions,
} from './ConfirmationDialog';

const ConfirmationServiceContext =
	createContext<ConfirmationServiceContextType>(() => Promise.resolve());

export const useConfirmation = () => useContext(ConfirmationServiceContext);

export interface ConfirmationServiceOptions
	extends Omit<ConfirmationOptions, 'open'> {
	catchOnCancel?: boolean;
}

export type ConfirmationServiceContextType = (
	options: ConfirmationServiceOptions,
) => Promise<void>;

export type ConfirmationServiceProviderRef =
	IContextRef<ConfirmationServiceContextType>;

export type ConfirmationServiceProviderProps = {
	ref?: ConfirmationServiceProviderRef;
	/**
	 * Children or children function
	 */
	children: IContextChildren<ConfirmationServiceContextType>;
};

const ConfirmationServiceProviderInner = (
	{children}: ConfirmationServiceProviderProps,
	ref: ConfirmationServiceProviderRef,
) => {
	const [catchOnCancel, setCatchOnCancel] = useState(false);
	const [confirmationState, setConfirmationState] =
		useState<ConfirmationServiceOptions>();

	const awaitingPromiseRef = useRef<{
		resolve: (...args: any) => void | Promise<void>;
		reject: () => void;
	}>();

	const openConfirmation = useCallback(
		({catchOnCancel = false, ...options}: ConfirmationServiceOptions) => {
			setCatchOnCancel(catchOnCancel);
			setConfirmationState(options);
			return new Promise<void>((resolve, reject) => {
				awaitingPromiseRef.current = {resolve, reject};
			});
		},
		[],
	);

	const handleClose = useConstCallback(() => {
		catchOnCancel && awaitingPromiseRef.current?.reject();
		awaitingPromiseRef.current = undefined;
		setConfirmationState(undefined);
	});

	const handleSubmit = useCallback((...args: any) => {
		awaitingPromiseRef.current?.resolve(...args);
		setConfirmationState(undefined);
	}, []);

	useImperativeHandle(ref, () => openConfirmation, [openConfirmation]);

	return (
		<>
			<ConfirmationServiceContext.Provider value={openConfirmation}>
				{renderChildrenOrChildrenFunction(children, openConfirmation)}
			</ConfirmationServiceContext.Provider>

			<ConfirmationDialog
				{...(confirmationState as ConfirmationServiceOptions)}
				open={confirmationState != null}
				onSubmit={handleSubmit}
				onClose={handleClose}
			/>
		</>
	);
};

export const ConfirmationServiceProviderPrimitive = React.forwardRef<
	ConfirmationServiceContextType,
	ConfirmationServiceProviderProps
>(ConfirmationServiceProviderInner);

/**
 * Confirmation service provider
 * @example
 * <ConfirmationServiceProvider ref={confirmationRef}>
 * 	...
 * </ConfirmationServiceProvider>
 * @example
 * <ConfirmationServiceProvider ref={confirmationRef}>
 * 	{(confirm) => (
 * 		...
 * 	)}
 * </ConfirmationServiceProvider>
 */
export const ConfirmationServiceProvider = ConfirmationServiceProviderPrimitive;

export default ConfirmationServiceProvider;
